import PropTypes from 'prop-types'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { POST_CALIBRATION_FIELD_MAP } from '../../../variable-maps'
import { useScrollTop } from '../../../utils/useScrollTop'
import { PhotoInput } from '@components/photo'
import { IdDisplay } from '@components/id-display'
import { StickyFooter } from '@components/sticky-footer'
import { OperationType, StatusEnum } from '../../../enums/enums'
import { FileUpload } from '@components/file-upload'
import { Logger } from '../../../logger'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { AssignedUser } from '@components/assigned-user'
import {
  AdasBox,
  AdasButton,
  AdasCheckbox,
  AdasContainer,
  AdasFormControl,
  AdasFormControlLabel,
  AdasFormGroup,
  AdasInputAdornment,
  AdasTextField,
  AdasTypography
} from '@components/wrapper-components'
import { getPostCalibration, submitPostCalibration, getInvoiceByReferenceNumber, getWorkOrderByReferenceNumber } from '../../../api/api'
import { FILE_UPLOAD, PARAM_NAMES, REDIRECT_URLS } from '../../../constants'

export const PostCalibrationPage = (props) => {
  useScrollTop()
  const { [PARAM_NAMES.REFERENCE_NUMBER]: referenceNumber } = useParams()
  const { fromInvoice = false } = props
  const history = useHistory()

  const setLoading = useLoadingStore((store) => store.setLoading)
  const [workOrderData, setWorkOrderData] = useState({}) //Object holds WorkOrder/Invoice data
  const orderClosed = workOrderData.id && StatusEnum[workOrderData.status] === StatusEnum.CLOSED
  // A client user reads the set-up like an invoice view does: every input off, nothing saved.
  const isReadOnlyUser = useIsReadOnly()
  const viewOnly = fromInvoice || isReadOnlyUser
  const [vehicleData, setVehicleData] = useState({})
  const loading = useLoadingStore((store) => store.loading)
  const [dataUrls, setDataUrls] = useState({
    dashPostCalibrationImageUrl: {},
    calibrationRequiredReport: '',
    postTestScanReport: ''
  })

  const vehicleName = `${vehicleData.make} ${vehicleData.model} ${vehicleData.trim}`

  const [postCalInputValues, setPostCalInputValues] = useState({
    odo_current: '',
    odo_stop: '',
    is_safety_systems_active: false,
    is_acc_dist_setting_1: false,
    is_acc_dist_setting_2: false,
    is_acc_dist_setting_3: false,
    is_acc_dist_setting_4: false,
    is_acc_speed_adj_down: false,
    is_acc_speed_adj_up: false,
    is_bs_left_alert: false,
    is_bs_right_alert: false,
    is_ld_alert: false,
    is_ld_no_alert: false,
    is_lka_steer_corr: false,
    is_lka_no_steer_corr: false,
    is_cam_rear_pivot_lines: false,
    is_cam_rear_img_focused: false,
    is_pa_alert_front: false,
    is_pa_alert_rear: false,
    is_apc_parks_in_space: false,
    is_cta_fl_approach_kit: false,
    is_cta_rl_approach_kit: false,
    is_cta_fr_approach_kit: false,
    is_cta_rr_approach_kit: false,
    is_cam_360_stitched: false,
    is_cam_360_focused: false,
    is_col_alert_brake: false,
    is_attach_wheel_alignment_rep: false,
    is_post_test_scn_rpt: false,
    // CAR-3639: the checkbox ("Post Test Drive Performed by:") and the driver picker
    // are decoupled. The checkbox owns this boolean; AssignedUser owns the driver
    // object in post_test_drive_performed_by (no longer clobbered by a raw boolean).
    is_post_test_drive_performed: false,
    post_test_drive_performed_by: false
  })

  const isSubmitEnabled =
    !viewOnly &&
    !orderClosed &&
    [
      postCalInputValues.is_safety_systems_active,
      postCalInputValues.odo_stop,
      postCalInputValues.is_post_test_scn_rpt,
      dataUrls.dashPostCalibrationImageUrl?.id
    ].every((value) => !!value)

  const redirectToForbiddenPage = () => {
    history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)
  }

  const handleFormInputChange = useCallback((e) => {
    let value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    if (e.target.name === 'odo_stop') {
      let digitsOnly = value.replace(/[^0-9]/g, '')
      digitsOnly = digitsOnly.slice(0, 7)
      value = digitsOnly
    }

    setPostCalInputValues((prevState) => {
      const updatedState = {
        ...prevState,
        [e.target.name]: value
      }
      return updatedState
    })
  }, [])

  const handlePostTestDriveChange = useCallback((value) => {
    setPostCalInputValues((prevState) => {
      const updatedState = {
        ...prevState,
        post_test_drive_performed_by: value
      }
      return updatedState
    })
  }, [])

  const savePostCalibration = useCallback(
    async (complete) => {
      const postCalDataSubmit = {
        ...postCalInputValues,
        is_postcalibration_complete: complete,
        post_test_drive_performed_by:
          postCalInputValues.post_test_drive_performed_by?.id > 0 ? postCalInputValues.post_test_drive_performed_by : null
      }

      const params = {
        data: postCalDataSubmit,
        workorderId: workOrderData.id
      }

      return submitPostCalibration(params)
    },
    [postCalInputValues, workOrderData.id]
  )

  // A client user can change nothing here, so there is nothing to save on the way out, and
  // the save would be a refused write.
  const returnToSummaryHandler = useCallback(async () => {
    if (isReadOnlyUser) {
      history.goBack()
      return
    }
    if (!workOrderData.is_postcalibration_complete) {
      await savePostCalibration(false)
    } else if (workOrderData.is_postcalibration_complete) {
      await savePostCalibration(true)
    }

    history.goBack()
  }, [fromInvoice, history, savePostCalibration, workOrderData, isReadOnlyUser])

  const submit = useCallback(async () => {
    const response = await savePostCalibration(true)
    if (response?.status === 200 || response?.status === 201) {
      history.goBack()
    }
  }, [fromInvoice, history, savePostCalibration, workOrderData])

  const removeFileHandler = useCallback((fileName) => {
    if (fileName) {
      setDataUrls((prevState) => {
        const dataUrls = {
          ...prevState,
          [fileName]: null
        }
        return dataUrls
      })
    }
  }, [])

  const requestPostCalibration = useCallback(async (params) => {
    try {
      setLoading(true)
      const response = await getPostCalibration(params)
      let existingPostCalibrationValues = {}
      if (response.status === 200) {
        const existingPostCalibration = response.data
        Object.keys(existingPostCalibration).forEach((key) => {
          if (existingPostCalibration[key]) {
            existingPostCalibrationValues[key] = existingPostCalibration[key]
          }
        })

        if (Object.keys(existingPostCalibrationValues).length !== 0) {
          setPostCalInputValues((preValues) => {
            const values = {
              ...preValues,
              ...existingPostCalibrationValues
            }
            // CAR-3639: reflect a previously-saved driver in the (decoupled) checkbox.
            values.is_post_test_drive_performed = !!values.post_test_drive_performed_by
            return values
          })
        }
        Object.keys(POST_CALIBRATION_FIELD_MAP).forEach((key) => {
          if (existingPostCalibration[key]) {
            const attachmentDetail = existingPostCalibration[key]
            setDataUrls((prevState) => {
              const imageUrls = {
                ...prevState,
                [POST_CALIBRATION_FIELD_MAP[key]]: attachmentDetail
              }
              return imageUrls
            })
          }
        })
      }
    } catch (error) {
      Logger.error({ message: error, payload: { params } })
    } finally {
      setLoading(false)
    }
  }, [])

  const refresh = useCallback(
    (refreshedData) => {
      if (refreshedData) {
        const propertyName = refreshedData.imageLocation
        if (refreshedData.operation === OperationType.ROTATE) {
          setDataUrls((prevState) => ({
            ...prevState,
            [propertyName]: {
              ...prevState[propertyName],
              img_rotation: refreshedData.img_rotation
            }
          }))
        }
        if (refreshedData.operation === OperationType.DELETE) {
          setDataUrls((prevState) => ({
            ...prevState,
            [propertyName]: null
          }))
        }
        if (refreshedData.operation === OperationType.RETAKE) {
          const imageItemDetail = {
            img_rotation: refreshedData.img_rotation,
            id: refreshedData.attachmentId
          }

          setDataUrls((prevState) => ({
            ...prevState,
            [propertyName]: imageItemDetail
          }))
        }
      } else {
        const params = {
          workorderId: workOrderData.id
        }
        requestPostCalibration(params)
      }
    },
    [requestPostCalibration, workOrderData.id]
  )

  useEffect(() => {
    workOrderData?.id && refresh()
  }, [refresh])

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (fromInvoice) {
          let response = await getInvoiceByReferenceNumber({ referenceNumber })
          if (response?.status === 200) {
            if (!response.data?.invoiceDto) {
              redirectToForbiddenPage()
              return
            }
            response.data.invoiceDto.id = response.data.workOrderDto.id // workOrderId
            setWorkOrderData(response.data.invoiceDto)
            setVehicleData(response.data.invoiceDto.vehicle)
          } else {
            //Given referenceNumber doesnot exist or no permission granted
            redirectToForbiddenPage()
            return
          }
        } else {
          let response = await getWorkOrderByReferenceNumber({ referenceNumber })
          if (response?.status === 200) {
            //Url Tampared i.e invoice/quote record tried to open in workorder path
            if (StatusEnum[response.data?.workOrder?.status] === StatusEnum.MOVED) {
              redirectToForbiddenPage()
              return
            }
            setWorkOrderData(response.data.workOrder)
            setVehicleData(response.data.workOrder.vehicle)
          } else {
            //Given referenceNumber doesnot exist or no permission granted
            redirectToForbiddenPage()
            return
          }
        }
      } catch (error) {
        Logger.error({
          message: error,
          payload: {
            referenceNumber
          }
        })
      }
    }
    if (referenceNumber) fetchData()
  }, [referenceNumber])

  const generatePhotoInputProps = (fieldLocation, labelText, title) => {
    const fieldData = dataUrls[fieldLocation] || {}

    return {
      labelText,
      imageLocation: fieldLocation,
      workorderId: workOrderData.id,
      onUpdate: refresh,
      disabled: orderClosed || viewOnly,
      properties: fieldData,
      imageId: fieldData.id || '',
      img_rotation: fieldData.img_rotation || 0,
      title: title
    }
  }

  const sectionTitle = 'text-[12px] font-semibold uppercase tracking-[1px] text-[#2C3D4D] mb-3'

  return (
    <AdasContainer maxWidth={false}>
      {!loading && (
        <AdasBox className='mt-5 mb-6'>
          <button type='button' onClick={returnToSummaryHandler} className='mb-3 text-[13px] font-medium text-[#1F6FEB] hover:underline'>
            Back to WO Summary
          </button>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white px-5 py-4'>
            {workOrderData.reference_number && (
              <AdasBox className='mb-2 flex items-center justify-between gap-3'>
                <AdasTypography className='text-[12px] font-semibold uppercase tracking-[1px] text-[#6B7280]'>
                  Post Calibration
                </AdasTypography>
                <AdasTypography className='text-[12px] font-semibold uppercase tracking-wide text-[#2C3D4D] text-right truncate'>
                  {vehicleName}
                </AdasTypography>
              </AdasBox>
            )}
            <AdasTypography className='text-[20px] font-bold text-[#0A1628]'>Post Calibration Checklist</AdasTypography>
            {workOrderData.reference_number && (
              <AdasBox className='mt-1'>
                <IdDisplay
                  itemId={workOrderData.reference_number}
                  roNumber={workOrderData.repair_order_num}
                  itemType={fromInvoice ? 'Invoice' : 'WO'}
                />
              </AdasBox>
            )}
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Safety Systems *</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_safety_systems_active}
                    onChange={handleFormInputChange}
                    name='is_safety_systems_active'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Verify all vehicle safety systems are activated'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasTypography className='mt-6 mb-3 text-[18px] font-bold text-[#0A1628]'>Dynamic System Verification</AdasTypography>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Adaptive Cruise Control (ACC)</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_acc_dist_setting_1}
                    onChange={handleFormInputChange}
                    name='is_acc_dist_setting_1'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Distance Setting 1'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_acc_dist_setting_2}
                    onChange={handleFormInputChange}
                    name='is_acc_dist_setting_2'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Distance Setting 2'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_acc_dist_setting_3}
                    onChange={handleFormInputChange}
                    name='is_acc_dist_setting_3'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Distance Setting 3'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_acc_dist_setting_4}
                    onChange={handleFormInputChange}
                    name='is_acc_dist_setting_4'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Distance Setting 4'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_acc_speed_adj_down}
                    onChange={handleFormInputChange}
                    name='is_acc_speed_adj_down'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Cruise Control Speed Adjust Down'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_acc_speed_adj_up}
                    onChange={handleFormInputChange}
                    name='is_acc_speed_adj_up'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Cruise Control Speed Adjust Up'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Blind Spot</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_bs_left_alert}
                    onChange={handleFormInputChange}
                    name='is_bs_left_alert'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Left Blind Spot Alert'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_bs_right_alert}
                    onChange={handleFormInputChange}
                    name='is_bs_right_alert'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Right Blind Spot Alert'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Cross Traffic Alert</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_cta_fl_approach_kit}
                    onChange={handleFormInputChange}
                    name='is_cta_fl_approach_kit'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Front Left Approach'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_cta_fr_approach_kit}
                    onChange={handleFormInputChange}
                    name='is_cta_fr_approach_kit'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Front Right Approach'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_cta_rl_approach_kit}
                    onChange={handleFormInputChange}
                    name='is_cta_rl_approach_kit'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Rear Left Approach'
              />

              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_cta_rr_approach_kit}
                    onChange={handleFormInputChange}
                    name='is_cta_rr_approach_kit'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Rear Right Approach'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Lane Departure Warning</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_ld_alert}
                    onChange={handleFormInputChange}
                    name='is_ld_alert'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Audible/Visual/Haptic Alert'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Lane Keep Assist</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_lka_steer_corr}
                    onChange={handleFormInputChange}
                    name='is_lka_steer_corr'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Vehicle Steering Correction (No Turn Signal)'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Park Assist</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_pa_alert_front}
                    onChange={handleFormInputChange}
                    name='is_pa_alert_front'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Front Audible/Visual/Haptic Alert'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_pa_alert_rear}
                    onChange={handleFormInputChange}
                    name='is_pa_alert_rear'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Rear Audible/Visual/Haptic Alert'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Active Park Assist</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_apc_parks_in_space}
                    onChange={handleFormInputChange}
                    name='is_apc_parks_in_space'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Vehicle Parks within Designated Space'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>360 Around View Camera</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_cam_360_stitched}
                    onChange={handleFormInputChange}
                    name='is_cam_360_stitched'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Images are stitched together (no overlaps/gaps)'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_cam_360_focused}
                    onChange={handleFormInputChange}
                    name='is_cam_360_focused'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Images are focused'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Rear View Camera</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_cam_rear_pivot_lines}
                    onChange={handleFormInputChange}
                    name='is_cam_rear_pivot_lines'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Directional Pivot Lines Turn w/Steering'
              />
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_cam_rear_img_focused}
                    onChange={handleFormInputChange}
                    name='is_cam_rear_img_focused'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Images are Focused'
              />
            </AdasFormGroup>
          </AdasBox>
          <AdasTypography className='mt-6 mb-3 text-[18px] font-bold text-[#0A1628]'>Post Test Drive</AdasTypography>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Mileage *</AdasTypography>
            <AdasBox className='mx-auto grid grid-cols-2 gap-3 sm:gap-[64px]'>
              <AdasBox className='col-span-2 w-[calc(50%-6px)] sm:w-[calc(50%-32px)] justify-self-center'>
                <PhotoInput {...generatePhotoInputProps('dashPostCalibrationImageUrl', 'DASH POST CALIBRATION')} />
              </AdasBox>
            </AdasBox>
            <AdasBox className='mt-3 mb-3 flex items-center justify-between'>
              <AdasTypography className='text-[14px] text-[#0A1628]'>Odometer Pre Calibration</AdasTypography>
              <AdasTypography className='text-[14px] font-semibold text-[#0A1628]'>{postCalInputValues.odo_current} mi</AdasTypography>
            </AdasBox>
            <AdasBox className='flex items-center justify-between gap-3'>
              <AdasTypography className='text-[14px] text-[#0A1628]'>Odometer Post Test Drive</AdasTypography>
              <AdasFormControl className='w-full md:w-[40%]'>
                <AdasTextField
                  id='fuel level'
                  label='Enter Current Mileage'
                  name='odo_stop'
                  value={postCalInputValues.odo_stop}
                  onChange={handleFormInputChange}
                  variant='outlined'
                  className='bg-white'
                  InputProps={{
                    endAdornment: <AdasInputAdornment position='end'>MI</AdasInputAdornment>
                  }}
                  disabled={orderClosed || viewOnly}
                />
              </AdasFormControl>
            </AdasBox>
          </AdasBox>
          <AdasBox className='mb-4 rounded-xl border border-[#D1D5D8] bg-white p-5'>
            <AdasTypography className={sectionTitle}>Post Test Drive Scan Report</AdasTypography>
            <AdasFormGroup>
              <AdasFormControlLabel
                control={
                  <AdasCheckbox
                    checked={postCalInputValues.is_post_test_scn_rpt}
                    onChange={handleFormInputChange}
                    name='is_post_test_scn_rpt'
                    disabled={orderClosed || viewOnly}
                  />
                }
                label='Complete Post Test Drive Scan Report *'
              />
            </AdasFormGroup>
            <AdasBox className='mt-3'>
              <FileUpload
                variant='row'
                type='workorders'
                removeFile={() => removeFileHandler('postTestScanReport')}
                existingData={dataUrls.postTestScanReport}
                workOrderData={workOrderData}
                title={FILE_UPLOAD.TITLE.UPLOAD_POST_TEST_DRIVE_SCAN_REPORT}
                label={FILE_UPLOAD.TITLE.UPLOAD_POST_TEST_DRIVE_SCAN_REPORT}
                typeLabel={FILE_UPLOAD.TYPE_LABEL.POST_TEST_DRIVE_SCAN_REPORT}
                disabled={orderClosed || viewOnly}
              />
            </AdasBox>
            <AdasFormGroup className='flex-row items-center mt-3'>
              <AdasTypography className='text-[14px] text-[#0A1628] mr-2'>Post Test Drive Performed by:</AdasTypography>
              <AssignedUser
                initialAssignee={postCalInputValues.post_test_drive_performed_by || { value: false }}
                closedStatus={orderClosed || viewOnly}
                updatePostTestDriveBy={handlePostTestDriveChange}
                includeAllUsers
              />
            </AdasFormGroup>
          </AdasBox>
          <StickyFooter>
            <AdasBox className='flex-col sm:flex-row justify-center sm:justify-between flex items-center gap-4'>
              <AdasButton
                variant='outlined'
                aria-label='return to Summary'
                color='primary'
                onClick={returnToSummaryHandler}
                className='w-full sm:w-auto'
              >
                {orderClosed || viewOnly ? '' : 'SAVE & '}RETURN TO SUMMARY
              </AdasButton>

              <AdasButton
                disabled={!isSubmitEnabled}
                variant='contained'
                aria-label='Set up complete'
                color='primary'
                onClick={submit}
                className='w-full sm:w-auto'
              >
                CHECKLIST COMPLETE
              </AdasButton>
            </AdasBox>
          </StickyFooter>
        </AdasBox>
      )}
    </AdasContainer>
  )
}

PostCalibrationPage.propTypes = {
  fromInvoice: PropTypes.bool
}
